import { Calendar, Edit, MoreVertical, Tag, Trash2 } from "lucide-react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { GetDiscountsQuery } from "@/gql/graphql";

type Discount = GetDiscountsQuery["getDiscounts"][number];

function getDiscountDescription(discount: Discount, t: (key: string) => string): string {
  const rules = discount.rules;
  const items = rules?.items ?? [];

  if (items.length === 0) return "";

  // Build description from rules
  const parts = items.map((item) => {
    const valueType = item.valueType ?? "";
    const value = item.value ?? 0;

    let valueText = "";
    if (valueType === "PERCENTAGE") {
      valueText = `${value}%`;
    } else if (valueType === "FIXED") {
      const dollars = (value / 100).toFixed(2);
      valueText = `$${dollars}`;
    } else {
      valueText = `${value}`;
    }

    if (item.target.type === "ALL_SERVICES") {
      return `${valueText} ${t("list.description.off")} ${t("list.description.allServices")}`;
    }

    const serviceCount = item.target.serviceIds?.length ?? 0;
    const itemType =
      serviceCount === 1 ? t("list.description.service") : t("list.description.services");
    return `${valueText} ${t("list.description.off")} ${serviceCount} ${itemType}`;
  });

  return parts.join(", ");
}

function formatActivePeriod(discount: Discount, salonTimezone: string): string {
  const activePeriod = discount.conditions?.timeCondition?.activePeriod;
  const targetedPeriod = discount.conditions?.timeCondition?.targetedPeriod;
  const targetedStart = targetedPeriod?.startDatetime;
  const targetedEnd = targetedPeriod?.endDatetime;
  const startDatetime = activePeriod?.startDatetime ?? targetedStart;
  const endDatetime = activePeriod?.endDatetime ?? targetedEnd;

  if (!startDatetime || !endDatetime) {
    return "";
  }

  const formatDate = (isoString: string) => {
    const dateTime = DateTime.fromISO(isoString, { zone: "utc" }).setZone(salonTimezone);
    return dateTime.toFormat("MMM dd, yyyy");
  };

  return `${formatDate(startDatetime)} - ${formatDate(endDatetime)}`;
}

function getDiscountStatus(
  discount: Discount,
  salonTimezone: string
): "active" | "scheduled" | "expired" {
  const activePeriod = discount.conditions?.timeCondition?.activePeriod;
  const targetedPeriod = discount.conditions?.timeCondition?.targetedPeriod;
  const startDatetime = activePeriod?.startDatetime ?? targetedPeriod?.startDatetime;
  const endDatetime = activePeriod?.endDatetime ?? targetedPeriod?.endDatetime;

  if (!startDatetime || !endDatetime) {
    return discount.isActive ? "active" : "expired";
  }

  const now = DateTime.now().setZone(salonTimezone);
  const startDate = DateTime.fromISO(startDatetime, { zone: "utc" }).setZone(salonTimezone);
  const endDate = DateTime.fromISO(endDatetime, { zone: "utc" }).setZone(salonTimezone);

  // Check if past end date
  if (now > endDate) {
    return "expired";
  }

  // Check if before start date
  if (now < startDate) {
    return "scheduled";
  }

  // Within active period - check isActive flag
  return discount.isActive ? "active" : "expired";
}

interface DiscountListProps {
  discounts: Discount[];
  salonTimezone: string;
  onEdit?: (discount: Discount) => void;
  onDelete?: (discount: Discount) => void;
  onClick?: (discount: Discount) => void;
}

export function DiscountList({
  discounts,
  salonTimezone,
  onEdit,
  onDelete,
  onClick,
}: DiscountListProps) {
  const { t } = useTranslation("discounts");

  return (
    <div className="space-y-4">
      {discounts.map((discount) => {
        const status = getDiscountStatus(discount, salonTimezone);

        return (
          <Card
            key={discount.id}
            className="cursor-pointer hover:bg-accent/50 transition-colors"
            onClick={() => onClick?.(discount)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-1 p-2 rounded-lg bg-primary/10">
                    <Tag className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-lg">{discount.name}</h3>
                      {status === "active" && (
                        <Badge className="bg-green-500/10 text-green-700 hover:bg-green-500/20">
                          {t("list.active")}
                        </Badge>
                      )}
                      {status === "scheduled" && (
                        <Badge className="bg-blue-500/10 text-blue-700 hover:bg-blue-500/20">
                          {t("list.scheduled")}
                        </Badge>
                      )}
                      {status === "expired" && (
                        <Badge variant="secondary">{t("list.expired")}</Badge>
                      )}
                      {discount.autoApply ? (
                        <Badge variant="outline">{t("list.automatic")}</Badge>
                      ) : (
                        <Badge variant="outline">{t("list.code")}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {getDiscountDescription(discount, t)}
                    </p>
                    {formatActivePeriod(discount, salonTimezone) && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4" />
                        <span>{formatActivePeriod(discount, salonTimezone)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit?.(discount);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {t("list.actions.edit")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(discount);
                      }}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("list.actions.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
          </Card>
        );
      })}
    </div>
  );
}
